import { Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Crown, Swords, ListChecks, Sparkles } from 'lucide-react';

import { useProfileMeQuery, useMeQuery } from '@/services/qbotApi';
import { DeckGrid } from '@/components/cr/DeckGrid';
import { cn } from '@/lib/cn';
import type { QbotApiError } from '@/types/qbot';

function isQbotApiError(err: unknown): err is QbotApiError {
  return typeof err === 'object' && err !== null && 'data' in err;
}

interface NavTileProps {
  to: string;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  badge?: string;
}

function NavTile({ to, icon, title, subtitle, badge }: NavTileProps) {
  return (
    <Link
      to={to}
      className="flex items-center gap-3 rounded-xl bg-bg-secondary p-3 transition-opacity active:opacity-80"
    >
      <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-bg-tertiary text-brand">
        {icon}
      </span>
      <div className="flex flex-1 flex-col">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-fg">{title}</span>
          {badge && (
            <span className="rounded-full bg-brand/15 px-2 text-[10px] font-bold uppercase text-brand">
              {badge}
            </span>
          )}
        </div>
        <span className="text-[11px] text-fg-muted">{subtitle}</span>
      </div>
      <ChevronRight aria-hidden className="h-5 w-5 text-fg-muted" />
    </Link>
  );
}

/**
 * CR landing — shows the linked player's headline stats + current deck,
 * then routes the user out to detailed screens (battles, decks, coach).
 *
 * If the user hits this URL but has no linked tag yet (NOT_LINKED error),
 * we redirect to /cr/link rather than render a broken empty state.
 */
export function CRHome() {
  const { t } = useTranslation('cr');
  // /me decides linked status; /profile/me actually pulls CR data.
  const { data: identity } = useMeQuery();
  const { data: profile, isLoading, error } = useProfileMeQuery();

  // Not linked → bounce to picker. We rely on /me too because /profile/me
  // returns 404 in both "not linked" and "tag invalid" cases.
  if (identity && identity.linkedAccounts.length === 0) {
    const code = isQbotApiError(error) ? error.data?.error?.code : undefined;
    if (code === 'NOT_LINKED' || code === 'NOT_FOUND') {
      return <Navigate to="/cr/link" replace />;
    }
  }

  if (isLoading || !profile) {
    return <div className="px-4 py-6 text-sm text-fg-muted">{t('home.loading')}</div>;
  }

  const { player } = profile;
  const isPro = identity?.tier === 'pro' || identity?.tier === 'admin';

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto px-4 py-4">
      {/* Player header */}
      <header className="flex items-center gap-3 rounded-xl bg-bg-secondary p-4">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-brand/15 text-brand">
          <Crown className="h-7 w-7" strokeWidth={2.5} />
        </div>
        <div className="flex flex-1 flex-col min-w-0">
          <h1 className="truncate text-lg font-bold text-fg">{player.name}</h1>
          <span className="text-[11px] text-fg-muted tabular-nums">{player.tag}</span>
          <div className="mt-1 flex items-center gap-3 text-[11px] text-fg-muted">
            <span>
              <span className="font-bold text-fg">{player.trophies.toLocaleString()}</span> 🏆
            </span>
            <span>{player.arena?.name ?? '—'}</span>
          </div>
        </div>
      </header>

      {/* Current deck */}
      {player.currentDeck?.length === 8 && (
        <section className="rounded-xl bg-bg-secondary p-3">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-xs font-bold uppercase tracking-wider text-fg-muted">
              {t('home.currentDeck')}
            </h2>
            <Link
              to="/cr/decks/check"
              className="text-[11px] font-semibold text-brand transition-opacity active:opacity-70"
            >
              {t('home.checkDeck')} →
            </Link>
          </div>
          <DeckGrid cards={player.currentDeck} avgElixir={undefined} />
        </section>
      )}

      {/* Nav tiles */}
      <nav className="flex flex-col gap-2">
        <NavTile
          to="/cr/battles"
          icon={<Swords className="h-5 w-5" strokeWidth={2.5} />}
          title={t('home.tiles.battles.title')}
          subtitle={t('home.tiles.battles.subtitle')}
        />
        <NavTile
          to="/cr/decks/popular"
          icon={<Crown className="h-5 w-5" strokeWidth={2.5} />}
          title={t('home.tiles.popular.title')}
          subtitle={t('home.tiles.popular.subtitle')}
        />
        <NavTile
          to="/cr/decks/saved"
          icon={<ListChecks className="h-5 w-5" strokeWidth={2.5} />}
          title={t('home.tiles.saved.title')}
          subtitle={t('home.tiles.saved.subtitle')}
        />
        <NavTile
          to="/cr/coach"
          icon={<Sparkles className="h-5 w-5" strokeWidth={2.5} />}
          title={t('home.tiles.coach.title')}
          subtitle={t('home.tiles.coach.subtitle')}
          badge={isPro ? undefined : 'PRO'}
        />
      </nav>

      <Link
        to="/cr/link"
        className={cn(
          'mt-auto rounded-lg border border-border bg-bg-secondary py-2 text-center text-xs text-fg-muted',
          'transition-opacity active:opacity-70',
        )}
      >
        {t('home.changeTag')}
      </Link>
    </div>
  );
}
